import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  Modal,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import ErrorModal from "../../../../utils/ErrorModal";
import { viewPDF } from "../../../../utils/viewPDF";
import SuccessModal from "../../../../utils/SuccessModal";
import html2pdf from "html2pdf.js";
import { handleExportPDF } from "../../../../utils/exportToPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const PreviewRFQ = ({
  rfq,
  setStep,
  user,
  approvers,
  fileNames,
  selectVendors,
}) => {
  const [draft, setDraft] = useState();
  const [workItems, setWorkItems] = useState();
  const [approversData, setApproversData] = useState({
    purchaseHead: "",
    awardee1: "",
    awardee2: "",
  });
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [msg, setMsg] = useState();
  const handleClose = () => setOpen(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const pdfRef = useRef();
  const [exporting, setExporting] = useState(false);

  // ID Logic: Formats the draftRfqId to match the SRS_B_YYYY_ID style
  const formatID = (idToFormat) => {
    if (!idToFormat) return "N/A";
    const year = new Date().getFullYear();
    // Extracts only numbers from the draftRfqId to ensure consistency
    const numericPart = idToFormat.toString().replace(/\D/g, "");
    return `SRS_B_${year}_${numericPart}`;
  };

  // This ensures the ID in Preview is identical to the one generated from the draft ID
  const displayRfqId = formatID(rfq?.draftRfqId);

  useEffect(() => {
    const getDraft = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/draft/get-by-id?draftRfqId=${rfq.draftRfqId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } }
        );
        setDraft(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    getDraft();
  }, [rfq.draftRfqId, user.accessToken]);

  useEffect(() => {
    const getWorkItems = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/draft/work-item?draftRfqId=${rfq.draftRfqId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } }
        );
        setWorkItems(res.data);
      } catch (error) {
        console.log(error);
      } finally {
        setItemsLoading(false);
      }
    };

    getWorkItems();
  }, [rfq.draftRfqId, user.accessToken]);

  useEffect(() => {
    const fetchAwardees = async () => {
      try {
        let awardee1Name = "N/A";
        if (approvers.awardee1) {
          const res1 = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${approvers.awardee1}`,
            {
              headers: { Authorization: `Bearer ${user.accessToken}` },
            }
          );
          awardee1Name = res1.data.name || "N/A";
        }

        const res2 = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${approvers.awardee2}`,
          {
            headers: { Authorization: `Bearer ${user.accessToken}` },
          }
        );

        const res3 = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${approvers.purchaseHead}`,
          {
            headers: { Authorization: `Bearer ${user.accessToken}` },
          }
        );

        setApproversData({
          ...approvers,
          purchaseHead: res3.data.name,
          awardee1: awardee1Name,
          awardee2: res2.data.name,
        });
      } catch (error) {
        console.error(error);
      }
    };

    if (approvers.awardee1 || approvers.awardee2) {
      fetchAwardees();
    }
  }, [approvers, user.accessToken]);

  const groupedWorkItems = workItems?.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {});

  const grandTotal =
    workItems &&
    workItems
      .reduce((sum, row) => sum + parseFloat(row.total || 0), 0)
      .toFixed(2);

  const handleNext = async () => {
    setBtnLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/rfq`,
        {
          rfqId: displayRfqId, // Sends the SRS formatted ID
          draftId: draft.draftRfqId,
          username: user.username,
          shortDesc: draft.shortDesc,
          brief: draft.brief,
          submissionDate: draft.submissionDate,
          document: draft.document,
          documentFilename: fileNames.document,
          name: draft.name,
          email: draft.email,
          dept: draft.dept.deptId,
          term1: draft.term1,
          term2: draft.term2,
          term3: draft.term3,
          term4: draft.term4,
          term5: draft.term5,
          term6: draft.term6,
          purchaseHead: approvers.purchaseHead,
          awardee1: approvers.awardee1,
          awardee2: approvers.awardee2,
          rfqWorkItems: workItems,
          vendors: selectVendors,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/draft?draftRfqId=${draft.draftRfqId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      setBtnLoading(false);
      setOpenConfirm(false);
      setOpen(true);
      setMsg("RFQ Published Successfully!");
    } catch (error) {
      console.log(error);
      setBtnLoading(false);
      setOpenConfirm(false);
      setErrorModal(true);
      setErrorMsg(
        error.response?.data?.Exception || "Failed to create tender."
      );
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const [date, time] = dateString.split("T");
    const [year, month, day] = date.split("-");
    const cleanTime = time?.split(".")[0];
    return `${day}/${month}/${year} ${cleanTime}`;
  };

  return (
    <div style={{ position: "relative" }}>
      {exporting && (
        <div className="uploading-div">
          Exporting PDF... This may take a while.{" "}
          <CircularProgress color="#fff" />
        </div>
      )}
      {btnLoading && (
        <div className="loading-div uploading-div">
          Submitting RFQ... <CircularProgress color="#fff" />
        </div>
      )}
      <h2>Preview RFQ</h2>
      <div style={{ display: "flex", justifyContent: "end", gap: 10 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" onClick={() => setOpenConfirm(true)}>
          Submit
        </Button>
        <Button
          className="btn-contained"
          sx={{ width: "200px" }}
          onClick={async () => {
            try {
              setExporting(true);
              await handleExportPDF(pdfRef, displayRfqId);
            } catch (error) {
              console.log(error);
            } finally {
              setExporting(false);
            }
          }}
        >
          Export PDF
        </Button>
      </div>
      <div ref={pdfRef}>
        <Box className="dashboard-container" px={10}>
          <table
            border={1}
            style={{
              borderCollapse: "collapse",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <thead style={{ backgroundColor: "#d8e9f0" }}>
              <tr>
                <th style={{ padding: "10px", textAlign: "left" }}>
                  Item Group
                </th>
                <th style={{ padding: "10px", textAlign: "right" }}>
                  Total (in ₹)
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedWorkItems &&
                Object.entries(groupedWorkItems).map(
                  ([group, items], index) => {
                    const groupTotal = items.reduce(
                      (sum, item) => sum + parseFloat(item.total || 0),
                      0
                    );
                    return (
                      <tr key={index}>
                        <td style={{ padding: "10px" }}>
                          {group || "Uncategorised"}
                        </td>
                        <td style={{ padding: "10px", textAlign: "right" }}>
                          {new Intl.NumberFormat("en-IN", {
                            style: "decimal",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(groupTotal)}
                        </td>
                      </tr>
                    );
                  }
                )}
              <tr style={{ fontWeight: "bold", backgroundColor: "#d8e9f0" }}>
                <td style={{ padding: "10px", textAlign: "right" }}>
                  Grand Total (in ₹):
                </td>
                <td style={{ padding: "10px", textAlign: "right" }}>
                  {new Intl.NumberFormat("en-IN", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography className="form-title">RFQ Details</Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              RFQ ID
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
                fontWeight: "bold"
              }}
            >
              {displayRfqId}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Short Description about RFQ
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderRight: "1px solid #000",
              }}
            >
              {draft && draft.shortDesc}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Submission Date
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && formatDateTime(draft.submissionDate)}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Comments/Brief about RFQ
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.brief}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Attachments
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.document && (
                <Button
                  sx={{
                    margin: "0 !important",
                    padding: "0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(draft.document, user)}
                >
                  {fileNames.document}
                </Button>
              )}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Name
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.name}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Email ID
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.email}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Department
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft.dept.name}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Delivery period (Lead Time)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft?.term1}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Payment Terms
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft?.term2}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Freight
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft?.term3}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Price Basis (Incoterms)
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft?.term4}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Warranty Period
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft?.term5}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Late Delivery Clause
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {draft && draft?.term6}
            </Grid2>
          </Grid2>
        </Box>

        <h2>Work Items Details</h2>
        <Box className="dashboard-container" px={10}>
          <table
            border={1}
            style={{
              borderCollapse: "collapse",
              width: "100%",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ padding: "20px" }}>Item Code</th>
                <th>Description</th>
                <th>Item Group</th>
                <th>UOM</th>
                <th>Quantity</th>
                <th>Rate (in &#8377;)</th>
                <th style={{ width: "100px" }}>PO Rate (in &#8377;)</th>
                <th>PO Number</th>
                <th>SAP PR</th>
                <th>GST (in %)</th>
                <th>Total (in &#8377;)</th>
              </tr>
            </thead>
            <tbody>
              {itemsLoading
                ? "Loading..."
                : groupedWorkItems &&
                  Object.entries(groupedWorkItems).map(
                    ([section, items], sectionIndex) => {
                      const sectionTotal = items.reduce(
                        (sum, item) => sum + item.rate * item.quantity,
                        0
                      );
                      return (
                        <React.Fragment key={sectionIndex}>
                          <tr
                            style={{
                              backgroundColor: "#d8e9f0",
                              fontWeight: "bold",
                            }}
                          >
                            <td
                              colSpan={10}
                              style={{ textAlign: "left", padding: "10px" }}
                            >
                              {section}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {sectionTotal &&
                                new Intl.NumberFormat("en-IN", {
                                  style: "decimal",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(sectionTotal)}
                            </td>
                          </tr>
                          {items
                            .sort((a, b) => a.itemOrder - b.itemOrder)
                            .map((item, index) => (
                              <tr key={index} style={{ textAlign: "center" }}>
                                <td style={{ padding: "20px" }}>{item.code}</td>
                                <td>{item.description}</td>
                                <td>{item.section}</td>
                                <td>{item.unit}</td>
                                <td>{item.quantity}</td>
                                <td>
                                  {new Intl.NumberFormat("en-IN", {
                                    style: "decimal",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(item.rate)}
                                </td>
                                <td>
                                  {new Intl.NumberFormat("en-IN", {
                                    style: "decimal",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(item.poRate)}
                                </td>
                                <td>{item.poNumber}</td>
                                <td>{item.sapPr}</td>
                                <td>{item.gst}</td>
                                <td>
                                  {new Intl.NumberFormat("en-IN", {
                                    style: "decimal",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(item.rate * item.quantity)}
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    }
                  )}
              <tr style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}>
                <td
                  colSpan={10}
                  style={{ textAlign: "right", paddingRight: "10px" }}
                >
                  Grand Total (in &#8377;):
                </td>
                <td
                  style={{
                    width: "150px",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  {new Intl.NumberFormat("en-IN", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography variant="h6">Approvers</Typography>
          <Grid2 container>
            {approversData && (
              <React.Fragment>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderTop: "1px solid #000" }}
                >
                  PURCHASE MANAGER
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{
                    borderTop: "1px solid #000",
                    borderRight: "1px solid #000",
                  }}
                >
                  {approversData.purchaseHead || "Loading..."}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  FINAL APPROVER 1
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {approversData.awardee1 || "Loading..."}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
                  FINAL APPROVER 2
                </Grid2>
                <Grid2
                  size={{ xs: 12, sm: 6 }}
                  className="preview-grid"
                  sx={{ borderRight: "1px solid #000" }}
                >
                  {approversData.awardee2 || "Loading..."}
                </Grid2>
              </React.Fragment>
            )}
          </Grid2>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography variant="h6">Vendors</Typography>
          <Box>{selectVendors.join(", ")}</Box>
        </Box>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" onClick={() => setOpenConfirm(true)}>
          Submit
        </Button>
      </div>

      <Modal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Are you sure you want to submit details for {displayRfqId}?
          </Typography>
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button
              className="btn-outlined"
              variant="outlined"
              onClick={() => setOpenConfirm(false)}
            >
              No
            </Button>
            <Button
              className="btn-contained"
              onClick={handleNext}
              disabled={btnLoading}
            >
              {btnLoading ? "Submitting..." : "Yes"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <SuccessModal
        open={open}
        handleClose={handleClose}
        msg={msg}
        nav={"/dashboard"}
      />

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default PreviewRFQ;